/**
 * Quick PIN reset — R27 (2026-04-30)
 * /pin/reset/:token — set a new 4-digit PIN.
 */
import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";

export default function QuickPinReset() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pin, setPin] = useState(["", "", "", ""]);
  const [confirm, setConfirm] = useState(["", "", "", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const refsA = [useRef(), useRef(), useRef(), useRef()];
  const refsB = [useRef(), useRef(), useRef(), useRef()];

  const fullPin = useMemo(() => pin.join(""), [pin]);
  const fullConf = useMemo(() => confirm.join(""), [confirm]);
  const canSubmit = fullPin.length === 4 && fullPin === fullConf && !submitting;

  const updateAt = (arr, refs, i, v) => {
    const d = (v || "").replace(/\D/g, "").slice(0, 1);
    arr[1]((prev) => {
      const next = [...prev];
      next[i] = d;
      return next;
    });
    if (d && i < 3) refs[i + 1].current?.focus();
  };

  const onKeyDown = (val, refs, i, e) => {
    if (e.key === "Backspace" && !val[i] && i > 0) refs[i - 1].current?.focus();
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post("/quick-pin/reset", { token, new_pin: fullPin });
      setDone(true);
      setTimeout(() => navigate("/pin"), 1800);
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Reset failed — link may have expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="quick-pin-reset-page" className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <h1 className="text-2xl font-black text-center">Set a new PIN</h1>
        <p className="text-slate-300 text-sm text-center mt-1">Choose a 4-digit code. Make it memorable.</p>
        <form onSubmit={submit} className="bg-white text-slate-900 rounded-2xl shadow-xl p-6 mt-6 space-y-5">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">New PIN</label>
            <div className="mt-1 flex gap-2 justify-center">
              {pin.map((d, i) => (
                <input
                  key={`a-${i}`}
                  data-testid={`reset-pin-a-${i}`}
                  ref={refsA[i]}
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={(e) => updateAt([pin, setPin], refsA, i, e.target.value)}
                  onKeyDown={(e) => onKeyDown(pin, refsA, i, e)}
                  className="w-14 h-14 text-center text-2xl font-black rounded-xl border-2 border-slate-300 bg-slate-50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 focus:bg-white"
                  autoFocus={i === 0}
                />
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Confirm PIN</label>
            <div className="mt-1 flex gap-2 justify-center">
              {confirm.map((d, i) => (
                <input
                  key={`b-${i}`}
                  data-testid={`reset-pin-b-${i}`}
                  ref={refsB[i]}
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={(e) => updateAt([confirm, setConfirm], refsB, i, e.target.value)}
                  onKeyDown={(e) => onKeyDown(confirm, refsB, i, e)}
                  className="w-14 h-14 text-center text-2xl font-black rounded-xl border-2 border-slate-300 bg-slate-50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 focus:bg-white"
                />
              ))}
            </div>
          </div>
          {error && <div data-testid="reset-error" className="text-sm font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</div>}
          {done && <div data-testid="reset-done" className="text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">PIN updated. Redirecting…</div>}
          <button
            type="submit"
            data-testid="reset-submit"
            disabled={!canSubmit || done}
            className={`w-full rounded-xl py-3 font-bold ${(canSubmit && !done) ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-slate-200 text-slate-500"}`}
          >
            {done ? "Done" : (submitting ? "Saving…" : "Save new PIN")}
          </button>
        </form>
      </div>
    </div>
  );
}
